import { CommonModule } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import { Component, inject, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { ApiService, showToast } from './api.service';

interface TrainingSession {
  id: number;
  name: string;
  description?: string | null;
  status: 'Draft' | 'Active' | 'Disabled';
  sample_count: number;
  submitted_count: number;
  approved_count: number;
  open_count: number;
  updated_at?: string;
  created_at?: string;
}

@Component({
  selector: 'app-admin-as-built-training',
  standalone: true,
  imports: [CommonModule, FormsModule],
  templateUrl: './admin-as-built-training.component.html',
  styleUrl: './admin-as-built-training.component.css'
})
export class AdminAsBuiltTrainingComponent {
  private api = inject(ApiService);
  private http = inject(HttpClient);
  private router = inject(Router);

  sessions = signal<TrainingSession[]>([]);
  isLoading = signal(false);
  isSaving = signal(false);
  name = signal('');
  description = signal('');

  ngOnInit(): void {
    this.loadSessions();
  }

  loadSessions(): void {
    this.isLoading.set(true);
    this.http.get<any>(`${this.api.baseUrl}/ai/list_as_built_training_sessions.php`, { headers: (this.api as any).getHeaders() })
      .subscribe({
        next: (res) => {
          this.isLoading.set(false);
          if (res?.success) this.sessions.set(res.data?.sessions || []);
        },
        error: () => {
          this.isLoading.set(false);
          showToast('error', 'Could not load training sets');
        }
      });
  }

  createSession(): void {
    const name = this.name().trim();
    if (!name) {
      showToast('error', 'Add a training set name');
      return;
    }
    this.isSaving.set(true);
    this.http.post<any>(`${this.api.baseUrl}/ai/save_as_built_training_session.php`, {
      name,
      description: this.description(),
      status: 'Draft'
    }, { headers: (this.api as any).getHeaders() }).subscribe({
      next: (res) => {
        this.isSaving.set(false);
        if (res?.success && res.data?.id) {
          this.name.set('');
          this.description.set('');
          showToast('success', 'Training set created');
          this.router.navigate(['/admin/ai/as-built/training/session', res.data.id]);
        } else {
          showToast('error', res?.message || 'Could not create training set');
        }
      },
      error: (e) => {
        this.isSaving.set(false);
        showToast('error', e.error?.message || 'Could not create training set');
      }
    });
  }

  openSession(session: TrainingSession): void {
    this.router.navigate(['/admin/ai/as-built/training/session', session.id]);
  }

  setStatus(session: TrainingSession, status: TrainingSession['status'], event: Event): void {
    event.stopPropagation();
    this.http.post<any>(`${this.api.baseUrl}/ai/save_as_built_training_session.php`, {
      id: session.id,
      name: session.name,
      description: session.description || '',
      status
    }, { headers: (this.api as any).getHeaders() }).subscribe({
      next: (res) => {
        if (res?.success) {
          showToast('success', status === 'Active' ? 'Training set turned on' : 'Training set updated');
          this.loadSessions();
        } else {
          showToast('error', res?.message || 'Could not update training set');
        }
      },
      error: () => showToast('error', 'Could not update training set')
    });
  }

  formatDate(value?: string): string {
    if (!value) return '';
    try {
      return new Date(value).toLocaleDateString('en-NZ', { timeZone: 'Pacific/Auckland' });
    } catch {
      return value;
    }
  }
}
